import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { DriverSummary, UserProfile } from '../types';
import { getCommonTimezones, getDisplayTimezone, setDisplayTimezone } from '../utils/time';
import './UserProfileModal.css';

interface UserProfileModalProps {
  open: boolean;
  mode: 'login' | 'profile' | 'settings';
  user: UserProfile | null;
  onClose: () => void;
  onLogin: () => void;
  onSave: (updates: Partial<UserProfile>) => void;
  onLinkSteam: () => void;
  onTimezoneChange?: (tz: string) => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ open, mode, user, onClose, onLogin, onSave, onLinkSteam, onTimezoneChange }) => {
  const [discordUsername, setDiscordUsername] = useState(user?.discordUsername || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
  const [youtube, setYoutube] = useState(user?.youtube || '');
  const [twitch, setTwitch] = useState(user?.twitch || '');
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [displayTimezone, setDisplayTimezoneState] = useState(getDisplayTimezone);
  const [driverProfile, setDriverProfile] = useState<DriverSummary | null>(null);
  const [loadingDriver, setLoadingDriver] = useState(false);
  const [driverError, setDriverError] = useState<string | null>(null);
  const commonTimezones = getCommonTimezones();

  useEffect(() => {
    if (!open) return;

    setDiscordUsername(user?.discordUsername || '');
    setAvatarUrl(user?.avatarUrl || '');
    setYoutube(user?.youtube || '');
    setTwitch(user?.twitch || '');
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setDisplayTimezoneState(getDisplayTimezone());
    setDriverError(null);
    setDriverProfile(null);
  }, [open, user]);

  useEffect(() => {
    const loadDriverProfile = async () => {
      if (!open || mode !== 'profile' || !user?.steamId) {
        return;
      }

      try {
        setLoadingDriver(true);
        setDriverError(null);
        const res = await api.getDrivers(user.steamId.trim());
        const matches = (res.data.drivers || []) as DriverSummary[];
        const bestMatch = matches[0] || null;
        setDriverProfile(bestMatch);

        if (!bestMatch) {
          setDriverError('No driver profile matched that Steam ID yet.');
        }
      } catch (err) {
        console.error('Failed to look up profile by steam id:', err);
        setDriverError('Unable to load the linked profile right now.');
      } finally {
        setLoadingDriver(false);
      }
    };

    loadDriverProfile();
  }, [mode, open, user?.steamId]);

  if (!open) {
    return null;
  }

  const handleLogin = (event: React.FormEvent) => {
    event.preventDefault();
    onLogin();
  };

  const handleTimezoneChange = (tz: string) => {
    setDisplayTimezoneState(tz);
    setDisplayTimezone(tz);
    onTimezoneChange?.(tz);
  };

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();
    onSave({
      discordUsername: discordUsername.trim(),
      avatarUrl: avatarUrl.trim(),
      youtube: youtube.trim(),
      twitch: twitch.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });
  };

  return (
    <div className="profile-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="profile-modal" onClick={(event) => event.stopPropagation()}>
        <div className="profile-modal-header">
          <div>
            <p className="profile-modal-kicker">Discord profile</p>
            <h2>{mode === 'login' ? 'Sign in with Discord' : mode === 'settings' ? 'Profile settings' : 'Profile'}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close profile dialog">
            ×
          </button>
        </div>

        {mode === 'login' && (
          <form className="profile-form" onSubmit={handleLogin}>
            <p className="profile-helper">Use your Discord identity to unlock the public profile experience.</p>
            <button className="primary-button" type="submit">Continue with Discord</button>
          </form>
        )}

        {mode === 'settings' && user && (
          <form className="profile-form" onSubmit={handleSave}>
            <div className="profile-avatar-preview">
              {user.avatarUrl ? <img src={user.avatarUrl} alt={user.displayName} /> : <span>{user.displayName.slice(0, 2).toUpperCase()}</span>}
            </div>
            <label>
              Display name
              <input value={discordUsername} onChange={(event) => setDiscordUsername(event.target.value)} />
            </label>
            <label>
              Avatar image URL
              <input value={avatarUrl} onChange={(event) => setAvatarUrl(event.target.value)} />
            </label>
            <label>
              YouTube
              <input value={youtube} onChange={(event) => setYoutube(event.target.value)} placeholder="@your-handle" />
            </label>
            <label>
              Twitch
              <input value={twitch} onChange={(event) => setTwitch(event.target.value)} placeholder="your-handle" />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label>
                First Name
                <input value={firstName} onChange={(event) => setFirstName(event.target.value)} placeholder="John" required />
              </label>
              <label>
                Last Name
                <input value={lastName} onChange={(event) => setLastName(event.target.value)} placeholder="Doe" required />
              </label>
            </div>
            <p className="profile-helper" style={{ fontSize: '0.85rem', color: 'var(--muted-text, #9ca3af)', margin: '-8px 0 8px 0' }}>
              ⚠️ First and last name are required along with Steam ID to be eligible for racing.
            </p>
            <label>
              Display timezone
              <select
                value={displayTimezone}
                onChange={(event) => handleTimezoneChange(event.target.value)}
                style={{ width: '100%', marginTop: '4px' }}
              >
                {!commonTimezones.includes(displayTimezone) && (
                  <option value={displayTimezone}>{displayTimezone}</option>
                )}
                {commonTimezones.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </label>
            <div className="steam-link-card">
              <div>
                <label>Steam account</label>
                <p className="profile-helper">{user.steamId ? `Linked to ${user.steamId}` : 'Not linked yet'}</p>
              </div>
              <button className="secondary-button" type="button" onClick={onLinkSteam}>Link Steam</button>
            </div>
            <button className="primary-button" type="submit">Save profile settings</button>
          </form>
        )}

        {mode === 'profile' && user && (
          <div className="profile-content">
            <div className="profile-card profile-card-hero">
              <div className="profile-avatar-preview large">
                {user.avatarUrl ? <img src={user.avatarUrl} alt={user.displayName} /> : <span>{user.displayName.slice(0, 2).toUpperCase()}</span>}
              </div>
              <div>
                <h3>{user.displayName}</h3>
                <p>@{user.discordUsername || 'discord-user'}</p>
                <div className="profile-socials">
                  {user.youtube ? <a href={user.youtube} target="_blank" rel="noreferrer">YouTube</a> : null}
                  {user.twitch ? <a href={user.twitch} target="_blank" rel="noreferrer">Twitch</a> : null}
                </div>
              </div>
            </div>

            {!user.steamId ? (
              <div className="profile-card profile-card-alert">
                <h4>Link Steam first</h4>
                <p>Connect your Steam profile in settings so we can find your driver profile and show your results.</p>
              </div>
            ) : (
              <div className="profile-card">
                {loadingDriver && <p>Looking up your driver profile…</p>}
                {driverError && <p>{driverError}</p>}
                {driverProfile && (
                  <div>
                    <h4>{driverProfile.driverName}</h4>
                    <p><strong>Nation:</strong> {driverProfile.nation}</p>
                    <p><strong>Sessions:</strong> {driverProfile.sessionCount}</p>
                    <p><strong>Last seen:</strong> {driverProfile.lastSeen ? new Date(driverProfile.lastSeen).toLocaleDateString() : 'Unknown'}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileModal;
